/** @format */

import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import {
  ResourceLink,
  ResourceReference,
  UserResourceReference,
} from "../../../types";
import ResourceDetails from "../resourceDetails/ResourceDetails";
import ResourceFeatures from "../resourceFeatures/ResourceFeatures";
import { selectBrand } from "../resourcesSlice";

import { ReactComponent as File } from "bootstrap-icons/icons/file-earmark.svg";
import { ReactComponent as FilePdf } from "bootstrap-icons/icons/file-earmark-pdf.svg";
import { ReactComponent as Globe } from "bootstrap-icons/icons/globe.svg";
import { ReactComponent as FilePpt } from "bootstrap-icons/icons/file-earmark-ppt-fill.svg";
import { ReactComponent as FileWord } from "bootstrap-icons/icons/file-earmark-word-fill.svg";
import { ReactComponent as FileExcel } from "bootstrap-icons/icons/file-earmark-excel-fill.svg";
import { ReactComponent as FilePlay } from "bootstrap-icons/icons/file-earmark-play-fill.svg";
import { ReactComponent as Printer } from "bootstrap-icons/icons/printer.svg";
import { useTranslation } from "react-i18next";
import ResourceFiles from "../resourceFiles/ResourceFiles";
import ResourceLinks from "../resourceLinks/ResourceLinks";
import { Alert, Button, Modal } from "react-bootstrap";
import { useUpdateResourceReferenceMutation } from "../../../app/services/resourcesApi";
import { ErrorBoundary } from "../../errorBoundary/ErrorBoundary";

export const fileIcon = (mimeType: string) => {
  switch (mimeType) {
    case "pdf":
      return (
        <span className="text-pdf">
          <FilePdf />
        </span>
      );
    case "webpage":
      return (
        <span className="text-info">
          <Globe />
        </span>
      );
    case "print":
      return (
        <span className="text-info">
          <Printer />
        </span>
      );
    case "mspowerpoint":
      return (
        <span className="text-ppt">
          <FilePpt />
        </span>
      );
    case "msword":
      return (
        <span className="text-word">
          <FileWord />
        </span>
      );
    case "msexcel":
      return (
        <span className="text-excel">
          <FileExcel />
        </span>
      );
    case "video":
      return (
        <span className="text-primary">
          <FilePlay />
        </span>
      );
    default:
      return <File />;
  }
};
type Props = {
  resourceRef: UserResourceReference | ResourceReference;
  preview: boolean; // true sets the component in preview mode
};

const ResourcePageUI: React.FC<Props> = ({ resourceRef, preview }) => {
  const { t: _ } = useTranslation();

  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const brand = useAppSelector(selectBrand());
  const [updateResourceReference] = useUpdateResourceReferenceMutation();
  const [authors, setAuthors] = useState<string>("");
  const [allAuthors, setAllAuthors] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!brand) return;
    import(`../../../json/${brand}Authors.json`).then(
      ({ default: authors }) => {
        setAllAuthors(authors);
      },
    );
  }, [brand]);

  useEffect(() => {
    setAuthors(resourceRef.Object.authors);
    // setAuthors(
    //   resourceRef?.Object?.authors.map(
    //     (id) => allAuthors.find((a) => String(a.id) === id)?.name || "",
    //   ) || [],
    // );
  }, [resourceRef?.Object?.authors, allAuthors]);

  return (
    <>
      {/* <Link to="../">Back</Link> */}

      <div className="mt-3 clearfix container px-0 pb-3">
        <div className="row">
          <div className="col-12 col-lg-6 mb-3">
            <ErrorBoundary
              fallback={
                <Alert variant="danger">
                  {_("error-boundary.failed-load-details")}
                </Alert>
              }
            >
              <ResourceDetails resource={resourceRef as ResourceReference} />
            </ErrorBoundary>
            {resourceRef.Type !== "BL" && (
              <>
                <ResourceFiles links={resourceRef.Object.links} />
                {isAuthenticated && !preview && (
                  <>
                    <Button variant="editing" onClick={() => setIsOpen(true)}>
                      {_("Edit Links")}
                    </Button>
                    <Modal
                      show={isOpen}
                      aria-label="edit-resource-ref-modal"
                      fullscreen={true}
                    >
                      <Modal.Header>
                        <h3>{_("Edit Links")}</h3>
                      </Modal.Header>
                      <ResourceLinks
                        propDefaultResourceLinks={resourceRef.Object.links}
                        closeModal={() => setIsOpen(false)}
                        buttonText={"save"}
                        saveLinks={async (
                          links: ResourceLink[],
                        ): Promise<boolean> => {
                          await updateResourceReference({
                            primaryKey: {
                              PK: resourceRef.PK,
                              SK: resourceRef.SK,
                            },
                            refInformation: {
                              locale: resourceRef.Locale,
                              type: resourceRef.Type,
                              code: resourceRef.Code,
                            },
                            changes: {
                              Object: {
                                links: links,
                              },
                            },
                          });

                          return true;
                        }}
                      />
                    </Modal>
                  </>
                )}
              </>
            )}
          </div>
          <div className="col-lg-1">
            <div className="vr px-0 d-none mx-auto d-lg-block h-100"></div>
          </div>
          <div className="col col-lg-5">
            <ErrorBoundary
              fallback={
                <Alert variant="danger">
                  {_("error-boundary.failed-load-features")}
                </Alert>
              }
            >
              <ResourceFeatures
                fileIcon={fileIcon}
                resource={resourceRef}
                authors={[authors]}
                preview={preview}
              />
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResourcePageUI;
